import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useState } from "react";
import {
  adminButtonOutlinedSx,
  adminButtonPrimarySx,
} from "../../../../../components/AdminDashboard/modalStyle";
import { useCategory } from "../../../../../context/category-store";
import { updateExpositorAsAdmin } from "../../../../../services/expositor.service";
import {
  normalizeInstagram,
  syncUserToKommo,
} from "../../../../../services/kommo.service";
import { ModalComponent } from "../../../../../ui-components/Modal";
import { useToast } from "../../../../../ui-components/ToastProvider";
import { parseDateHifen } from "../../../../../utils/parseDate";

interface Expositor {
  name: string;
  email: string;
  created_at: Date;
  is_admin: boolean;
  full_address: string;
  instagram_url: string;
  brand_name: string;
  brand_description: string;
  brand_items: string;
  category_id: number;
  address: string;
  address_number: string;
  address_complement: string;
  phone: string;
  cpf: string;
  cnpj: string;
  birthday: Date;
  zipcode: string;
  state: string;
  city: string;
  district: string;
  overdue: boolean;
  blocked: boolean;
  id: number;
}

export default function EditButton({
  id,
  updateEvents,
  expositor,
}: {
  id: number;
  updateEvents: Function;
  expositor: Expositor;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showToast } = useToast();
  const categories = useCategory((state) => state.categories);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: expositor.name || "",
    email: expositor.email || "",
    birthday: parseDateHifen(expositor.birthday) || "",
    city: expositor.city || "",
    state: expositor.state || "",
    zipcode: expositor.zipcode || "",
    district: expositor.district || "",
    address: expositor.address || "",
    address_number: expositor.address_number || "",
    address_complement: expositor.address_complement || "",
    phone: expositor.phone || "",
    cpf: expositor.cpf || "",
    cnpj: expositor.cnpj || "",
    brand_name: expositor.brand_name || "",
    instagram_url: expositor.instagram_url || "",
    brand_description: expositor.brand_description || "",
    brand_items: expositor.brand_items || "",
    category_id: expositor.category_id || "",
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInstagramBlur = () => {
    const normalized = normalizeInstagram(formData.instagram_url);
    setFormData((prev) => ({ ...prev, instagram_url: normalized }));
  };

  const handleSubmit = () => {
    const normalizedInstagram = normalizeInstagram(formData.instagram_url);
    const payload = {
      ...formData,
      instagram_url: normalizedInstagram,
      category_id: Number(formData.category_id),
    };

    updateExpositorAsAdmin(expositor.id, payload).then((response: any) => {
      if (response.status === 200) {
        showToast("Dados do expositor atualizados com sucesso!", { severity: "success" });
        handleClose();
        updateEvents();
        syncUserToKommo(expositor.id, {
          name: payload.name,
          instagram_url: normalizedInstagram,
          brand_name: payload.brand_name,
          brand_description: payload.brand_description,
          brand_items: payload.brand_items,
          cpf: payload.cpf,
          cnpj: payload.cnpj,
          city: payload.city,
          state: payload.state,
          phone: payload.phone,
        }).catch(() => {});
      } else {
        showToast(response.message || "Erro ao atualizar dados do expositor!", { severity: "error" });
      }
    }).catch(() => {
      showToast("Erro ao atualizar dados do expositor!", { severity: "error" });
    });
  };

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={`Editar Expositor: ${expositor.name}`}
        maxWidth={isMobile ? "95%" : 800}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Typography variant="subtitle1" sx={{ color: "#24324D", fontWeight: 600 }}>
            Dados Pessoais
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome"
                name="name"
                value={formData.name}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                name="birthday"
                type="date"
                value={formData.birthday}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CPF"
                name="cpf"
                value={formData.cpf}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CNPJ"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ color: "#24324D", fontWeight: 600 }}>
            Endereço
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CEP"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cidade"
                name="city"
                value={formData.city}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Estado"
                name="state"
                value={formData.state}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bairro"
                name="district"
                value={formData.district}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Endereço"
                name="address"
                value={formData.address}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número"
                name="address_number"
                value={formData.address_number}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Complemento"
                name="address_complement"
                value={formData.address_complement}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ color: "#24324D", fontWeight: 600 }}>
            Informações da Marca
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome da Marca"
                name="brand_name"
                value={formData.brand_name}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Instagram"
                name="instagram_url"
                value={formData.instagram_url}
                onChange={handleChange}
                onBlur={handleInstagramBlur}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição da Marca"
                name="brand_description"
                value={formData.brand_description}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Itens da Marca"
                name="brand_items"
                value={formData.brand_items}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="edit-category-label">Categoria da Marca</InputLabel>
                <Select
                  labelId="edit-category-label"
                  name="category_id"
                  value={formData.category_id.toString()}
                  label="Categoria da Marca"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, category_id: e.target.value }))
                  }
                >
                  {categories
                    .filter((x: any) => x.active)
                    .map((x: any) => (
                      <MenuItem key={x.id} value={x.id.toString()}>
                        {x.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: isMobile ? "column" : "row-reverse",
            justifyContent: isMobile ? "flex-end" : "flex-start",
            borderTop: "1px solid",
            borderColor: "divider",
            p: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={adminButtonPrimarySx}
            fullWidth={isMobile}
          >
            Salvar Alterações
          </Button>
          <Button
            onClick={handleClose}
            sx={adminButtonOutlinedSx}
            fullWidth={isMobile}
          >
            Cancelar
          </Button>
        </Box>
      </ModalComponent>
      <IconButton title="Editar expositor" onClick={handleOpen}>
        <EditIcon />
      </IconButton>
    </>
  );
}
